import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { ArrowLeftIcon, PlusIcon, TrashIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { documentService, clientService, articleService, invoiceTemplateService, companyService } from '../../api/services';
import { safeStorage } from '../../utils/safeStorage';
import {
  Document,
  Client,
  Article,
  InvoiceTemplate,
  LigneDocument,
  TypeDocument,
  TauxTva,
  ModePaiement,
  TVA_LABELS,
  TYPE_DOCUMENT_LABELS,
  MODE_PAIEMENT_LABELS,
} from '../../types';
import { formatTND, calculateLineTotals, calculateDocumentTotals } from '../../utils/formatters';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import toast from 'react-hot-toast';

interface DocumentFormData {
  typeDocument: TypeDocument;
  dateDocument: string;
  dateEcheance: string;
  clientId: number;
  modePaiement: ModePaiement;
  notes: string;
  referenceExterne: string;
  factureElectronique: boolean;
  appliquerTimbreFiscal: boolean;
  templateId: number;
  lignes: LigneFormData[];
}

interface LigneFormData {
  articleId: number;
  articleDesignation: string;
  quantite: number;
  prixUnitaireHT: number;
  tauxTva: TauxTva;
  remise: number;
  fodec: boolean;
}

interface ArticlePickerCellProps {
  articles: Article[];
  selectedArticleId: number;
  placeholder: string;
  onSelect: (article: Article) => void;
  onCreate: (designation: string) => Promise<Article | null>;
}

const ArticlePickerCell: React.FC<ArticlePickerCellProps> = ({
  articles, selectedArticleId, placeholder, onSelect, onCreate,
}) => {
  const selected = articles.find((a) => a.id === selectedArticleId) || null;
  const [query, setQuery] = useState<string>(selected?.designation || '');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // The dropdown is rendered through a portal at document.body so it escapes the
  // table's overflow-x-auto container (which implicitly clips the y-axis per the
  // CSS spec). Positioning is computed from the input's bounding rect.
  const [coords, setCoords] = useState<{ top: number; left: number; width: number }>({
    top: 0, left: 0, width: 288,
  });

  useEffect(() => {
    if (!open) setQuery(selected?.designation || '');
  }, [selected?.designation, open]);

  useLayoutEffect(() => {
    if (!open || !inputRef.current) return;
    const update = () => {
      const r = inputRef.current!.getBoundingClientRect();
      setCoords({
        top: r.bottom + window.scrollY,
        left: r.left + window.scrollX,
        width: Math.max(r.width, 288),
      });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? articles.filter((a) =>
        (a.designation || '').toLowerCase().includes(q) ||
        (((a as any).code as string) || a.reference || '').toLowerCase().includes(q))
    : articles;
  const exactMatch = q.length > 0 && articles.some((a) => (a.designation || '').toLowerCase() === q);

  const handleSelect = (a: Article) => {
    onSelect(a);
    setQuery(a.designation);
    setOpen(false);
  };

  const handleCreate = async () => {
    const created = await onCreate(query.trim());
    if (created) {
      setQuery(created.designation);
      setOpen(false);
    }
  };

  const showDropdown = open && (filtered.length > 0 || (q && !exactMatch));

  return (
    <div ref={wrapperRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className="input-field text-sm"
        data-testid="article-picker-input"
      />
      {showDropdown && createPortal(
        <div
          ref={dropdownRef}
          data-testid="article-picker-dropdown"
          style={{ position: 'absolute', top: coords.top, left: coords.left, width: coords.width, zIndex: 1000 }}
          className="mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto"
        >
          {filtered.slice(0, 20).map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => handleSelect(a)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-primary-50 hover:text-primary-700"
            >
              <span className="font-medium">{a.designation}</span>
              <span className="text-gray-500 ml-2 text-xs">
                ({((a as any).code as string) || a.reference || ''})
              </span>
            </button>
          ))}
          {q && !exactMatch && (
            <button
              type="button"
              onClick={handleCreate}
              className="w-full text-left px-3 py-2 text-sm text-primary-700 bg-primary-50 hover:bg-primary-100 border-t border-gray-200 flex items-center gap-2"
            >
              <PlusIcon className="h-4 w-4" />
              <span>Créer l'article «&nbsp;<span className="font-medium">{query.trim()}</span>&nbsp;»</span>
            </button>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export function DocumentForm() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [templates, setTemplates] = useState<InvoiceTemplate[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  // Montant du timbre paramétré sur la société : c'est lui que le backend applique,
  // l'afficher évite un aperçu de totaux qui diverge du document enregistré.
  const [companyTimbreFiscal, setCompanyTimbreFiscal] = useState(1.0);
  const [totals, setTotals] = useState({
    totalHT: 0,
    totalTva: 0,
    totalFodec: 0,
    totalRemise: 0,
    timbreFiscal: 0,
    totalTTC: 0,
    retenueSource: 0,
    netAPayer: 0,
  });

  const defaultType = (searchParams.get('type') as TypeDocument) || TypeDocument.FACTURE;

  const { register, handleSubmit, control, watch, setValue, reset, formState: { errors } } = useForm<DocumentFormData>({
    defaultValues: {
      typeDocument: defaultType,
      dateDocument: new Date().toISOString().split('T')[0],
      dateEcheance: '',
      clientId: 0,
      modePaiement: ModePaiement.VIREMENT,
      notes: '',
      referenceExterne: '',
      factureElectronique: false,
      appliquerTimbreFiscal: true,
      templateId: 0,
      lignes: [
        {
          articleId: 0,
          articleDesignation: '',
          quantite: 1,
          prixUnitaireHT: 0,
          tauxTva: TauxTva.TVA_19,
          remise: 0,
          fodec: true,
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'lignes',
  });

  const watchedLignes = watch('lignes');
  const watchedModePaiement = watch('modePaiement');
  const watchedTypeDocument = watch('typeDocument');
  const watchedDateDocument = watch('dateDocument');
  const watchedDateEcheance = watch('dateEcheance');
  const watchedTemplateId = watch('templateId');
  const watchedNotes = watch('notes');
  const watchedFactureElectronique = watch('factureElectronique');
  const watchedAppliquerTimbre = watch('appliquerTimbreFiscal');

  // Live PDF preview state
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState<boolean>(() => {
    return safeStorage.getItem('tunifact-form-preview') !== 'off';
  });
  const previewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewBlobUrlRef = useRef<string | null>(null);

  // Snapshot of fields used to decide whether to refetch the preview. We stringify
  // a stable subset — re-fetching only when the data that actually changes the PDF
  // changes (avoids spurious requests on focus/blur).
  const previewKey = useMemo(() => {
    return JSON.stringify({
      typeDocument: watchedTypeDocument,
      dateDocument: watchedDateDocument,
      dateEcheance: watchedDateEcheance,
      clientId: selectedClient?.id || 0,
      modePaiement: watchedModePaiement,
      templateId: watchedTemplateId,
      notes: watchedNotes,
      factureElectronique: watchedFactureElectronique,
      appliquerTimbreFiscal: watchedAppliquerTimbre,
      lignes: (watchedLignes || []).map((l) => ({
        articleId: l?.articleId,
        quantite: l?.quantite,
        prixUnitaireHT: l?.prixUnitaireHT,
        tauxTva: l?.tauxTva,
        remise: l?.remise,
        fodec: l?.fodec,
      })),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedTypeDocument, watchedDateDocument, watchedDateEcheance,
    selectedClient?.id, watchedModePaiement, watchedTemplateId,
    watchedNotes, watchedFactureElectronique, watchedAppliquerTimbre, watchedLignes,
  ]);

  // Load lookups (clients / articles / templates) FIRST, then — only in edit mode —
  // load the document and resolve the selected client against the freshly-loaded list.
  // These used to be two parallel effects; loadDocument would race ahead of clients[]
  // being populated and selectedClient would stay null, which silently disabled the
  // PDF preview (gated on selectedClient?.id) and zeroed out the retention-source row
  // until the user manually re-picked the client.
  useEffect(() => {
    (async () => {
      const lookups = await loadInitialData();
      if (isEdit && id) {
        await loadDocument(parseInt(id), lookups?.clients ?? []);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  // Recompute the totals card whenever any line value, the mode of payment, or the
  // selected client changes. We can't depend on `watchedLignes` directly: after a
  // reset() (the edit-mode case), react-hook-form keeps the same array reference and
  // mutates the values in place, so Object.is sees no change and the effect never
  // re-fires — leaving the totals frozen at the default-zeroed initial pass while the
  // per-line inline calc (re-rendered as JSX on every render) shows the right amount.
  // Snapshotting the watched values to a JSON string side-steps the reference check.
  const lignesSnapshot = JSON.stringify(
    (watchedLignes || []).map((l) => ({
      q: l?.quantite,
      pu: l?.prixUnitaireHT,
      tva: l?.tauxTva,
      remise: l?.remise,
      fodec: l?.fodec,
    })),
  );
  useEffect(() => {
    recalculateTotals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lignesSnapshot, watchedTypeDocument, selectedClient, watchedAppliquerTimbre, companyTimbreFiscal]);

  // Live PDF preview — debounce 600ms then call the preview endpoint.
  useEffect(() => {
    if (!showPreview) return;
    if (!selectedClient?.id) {
      setPreviewError('Sélectionnez un client pour voir l\'aperçu PDF.');
      return;
    }
    const validLignes = (watchedLignes || []).filter((l) => l && l.articleId > 0 && l.quantite > 0);
    if (validLignes.length === 0) {
      setPreviewError('Ajoutez au moins une ligne pour voir l\'aperçu PDF.');
      return;
    }
    if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);
    previewTimeoutRef.current = setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const lignes: LigneDocument[] = validLignes.map((l, idx) => {
          const calc = calculateLineTotals(l.quantite, l.prixUnitaireHT, l.tauxTva, l.fodec, l.remise);
          return {
            articleId: l.articleId,
            quantite: l.quantite,
            prixUnitaireHT: l.prixUnitaireHT,
            tauxTva: l.tauxTva,
            remise: l.remise,
            fodec: l.fodec,
            montantHT: calc.montantHT,
            montantTva: calc.montantTva,
            montantTTC: calc.montantTTC,
            montantFodec: calc.montantFodec,
            ordre: idx + 1,
          };
        });
        const draft: Partial<Document> = {
          typeDocument: watchedTypeDocument,
          dateDocument: watchedDateDocument,
          dateEcheance: watchedDateEcheance || undefined,
          clientId: selectedClient.id,
          modePaiement: watchedModePaiement,
          notes: watchedNotes || undefined,
          factureElectronique: Boolean(watchedFactureElectronique),
          appliquerTimbreFiscal: Boolean(watchedAppliquerTimbre),
          templateId: watchedTemplateId && watchedTemplateId > 0 ? watchedTemplateId : undefined,
          lignes,
          ...totals,
        };
        const blob = await documentService.previewPdf(draft, 'fr');
        const url = URL.createObjectURL(blob);
        // Revoke any previously generated blob so we don't leak memory while typing.
        if (previewBlobUrlRef.current) URL.revokeObjectURL(previewBlobUrlRef.current);
        previewBlobUrlRef.current = url;
        setPreviewUrl(url);
      } catch (err: any) {
        setPreviewError(err?.response?.data?.message || 'Aperçu indisponible');
      } finally {
        setPreviewLoading(false);
      }
    }, 600);
    return () => {
      if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey, totals.totalTTC, showPreview]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (previewBlobUrlRef.current) URL.revokeObjectURL(previewBlobUrlRef.current);
    };
  }, []);

  useEffect(() => {
    safeStorage.setItem('tunifact-form-preview', showPreview ? 'on' : 'off');
  }, [showPreview]);

  const loadInitialData = async (): Promise<{ clients: Client[]; articles: Article[]; templates: InvoiceTemplate[] } | null> => {
    try {
      const [clientsData, articlesData, templatesData, company] = await Promise.all([
        clientService.getAll({ size: 1000, actif: true }),
        articleService.getAll({ size: 1000, actif: true }),
        invoiceTemplateService.getAll().catch(() => [] as InvoiceTemplate[]),
        companyService.get().catch(() => null),
      ]);
      if (company?.timbreFiscal != null) setCompanyTimbreFiscal(Number(company.timbreFiscal));
      setClients(clientsData.content);
      setFilteredClients(clientsData.content);
      setArticles(articlesData.content);
      setTemplates(templatesData);
      if (!isEdit) {
        const def = templatesData.find((t) => t.isDefault) || templatesData[0];
        if (def) setValue('templateId', def.id);
        const preselectId = Number(searchParams.get('clientId') || '');
        if (preselectId > 0) {
          const c = clientsData.content.find((cc: Client) => cc.id === preselectId);
          if (c) {
            setSelectedClient(c);
            setClientSearch(c.raisonSociale);
            setValue('clientId', c.id);
          }
        }
      }
      return { clients: clientsData.content, articles: articlesData.content, templates: templatesData };
    } catch (error) {
      toast.error(t.messages.loadError);
      return null;
    }
  };

  const loadDocument = async (docId: number, clientList: Client[]) => {
    try {
      setLoading(true);
      const data = await documentService.getById(docId);
      const formData: DocumentFormData = {
        typeDocument: data.typeDocument,
        dateDocument: data.dateDocument?.split('T')[0] || '',
        dateEcheance: data.dateEcheance?.split('T')[0] || '',
        clientId: data.clientId,
        modePaiement: data.modePaiement,
        notes: data.notes || '',
        referenceExterne: data.referenceExterne || '',
        factureElectronique: Boolean(data.factureElectronique),
        appliquerTimbreFiscal: data.appliquerTimbreFiscal !== false,
        templateId: data.templateId || 0,
        lignes: data.lignes.map((l) => ({
          articleId: l.articleId,
          articleDesignation: l.articleDesignation || '',
          quantite: l.quantite,
          prixUnitaireHT: l.prixUnitaireHT,
          tauxTva: l.tauxTva,
          remise: l.remise ?? 0,
          fodec: l.fodec,
        })),
      };
      reset(formData);
      setClientSearch(data.clientRaisonSociale || '');
      // Resolve from the explicit client list passed in (the `clients` state may not
      // have re-rendered yet during this same async chain).
      const client = clientList.find((c) => c.id === data.clientId);
      if (client) setSelectedClient(client);
    } catch (error) {
      toast.error(t.messages.loadError);
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const recalculateTotals = () => {
    if (!watchedLignes) return;

    const lineTotals = watchedLignes.map((l) =>
      calculateLineTotals(
        l.quantite || 0,
        l.prixUnitaireHT || 0,
        l.tauxTva || TauxTva.TVA_19,
        l.fodec || false,
        l.remise || 0
      )
    );

    const tauxRS = selectedClient?.tauxRetenueSource || 0;
    const newTotals = calculateDocumentTotals(
      lineTotals,
      watchedTypeDocument,
      tauxRS,
      companyTimbreFiscal,
      watchedAppliquerTimbre !== false
    );
    setTotals(newTotals);
  };

  const handleClientSearch = (query: string) => {
    setClientSearch(query);
    setShowClientDropdown(true);
    if (query.length >= 1) {
      const filtered = clients.filter(
        (c) =>
          c.raisonSociale.toLowerCase().includes(query.toLowerCase()) ||
          c.code.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredClients(filtered);
    } else {
      setFilteredClients(clients);
    }
  };

  const selectClient = (client: Client) => {
    setSelectedClient(client);
    setClientSearch(client.raisonSociale);
    setValue('clientId', client.id);
    setShowClientDropdown(false);
  };

  const handleQuickCreateClient = async () => {
    const name = clientSearch.trim();
    if (!name) return;
    try {
      const created = await clientService.create({ raisonSociale: name });
      setClients((prev) => [...prev, created]);
      selectClient(created);
      toast.success(`Client « ${created.raisonSociale} » créé`);
    } catch (e) {
      toast.error("Impossible de créer le client");
    }
  };

  const handleQuickCreateArticle = async (index: number, designation: string) => {
    const name = designation.trim();
    if (!name) return null;
    try {
      const created = await articleService.quickCreate(name);
      setArticles((prev) => [...prev, created]);
      selectArticle(index, created.id);
      toast.success(`Article « ${created.designation} » créé`);
      return created;
    } catch (e) {
      toast.error("Impossible de créer l'article");
      return null;
    }
  };

  const clientExactMatchExists = () => {
    const q = clientSearch.trim().toLowerCase();
    return q.length > 0 && clients.some((c) => c.raisonSociale.toLowerCase() === q);
  };

  const selectArticle = (index: number, articleId: number) => {
    const article = articles.find((a) => a.id === articleId);
    if (article) {
      setValue(`lignes.${index}.articleId`, article.id);
      setValue(`lignes.${index}.articleDesignation`, article.designation);
      setValue(`lignes.${index}.prixUnitaireHT`, article.prixUnitaireHT);
      setValue(`lignes.${index}.tauxTva`, article.tauxTva);
      setValue(`lignes.${index}.fodec`, article.fodec);
    }
  };

  const addLine = () => {
    append({
      articleId: 0,
      articleDesignation: '',
      quantite: 1,
      prixUnitaireHT: 0,
      tauxTva: TauxTva.TVA_19,
      remise: 0,
      fodec: true,
    });
  };

  const onSubmit = async (data: DocumentFormData) => {
    if (!data.clientId || data.clientId === 0) {
      toast.error('Veuillez sélectionner un client');
      return;
    }

    const validLignes = data.lignes.filter((l) => l.articleId > 0 && l.quantite > 0);
    if (validLignes.length === 0) {
      toast.error('Veuillez ajouter au moins une ligne');
      return;
    }

    try {
      setSaving(true);
      const lignes: LigneDocument[] = validLignes.map((l, idx) => {
        const calc = calculateLineTotals(l.quantite, l.prixUnitaireHT, l.tauxTva, l.fodec, l.remise);
        return {
          articleId: l.articleId,
          quantite: l.quantite,
          prixUnitaireHT: l.prixUnitaireHT,
          tauxTva: l.tauxTva,
          remise: l.remise,
          fodec: l.fodec,
          montantHT: calc.montantHT,
          montantTva: calc.montantTva,
          montantTTC: calc.montantTTC,
          montantFodec: calc.montantFodec,
          ordre: idx + 1,
        };
      });

      const document: Partial<Document> = {
        typeDocument: data.typeDocument,
        dateDocument: data.dateDocument,
        dateEcheance: data.dateEcheance || undefined,
        clientId: data.clientId,
        modePaiement: data.modePaiement,
        notes: data.notes || undefined,
        referenceExterne: data.referenceExterne || undefined,
        factureElectronique: data.factureElectronique,
        appliquerTimbreFiscal: data.appliquerTimbreFiscal,
        templateId: data.templateId && data.templateId > 0 ? data.templateId : undefined,
        lignes,
        ...totals,
      };

      const saved = isEdit && id
        ? await documentService.update(parseInt(id), document)
        : await documentService.create(document);
      toast.success(t.messages.saveSuccess);
      // Redirige vers la facture en mode consultation (lecture seule) plutôt que
      // de revenir à la liste, pour que l'utilisateur voie le document enregistré.
      navigate(`/documents/view/${saved.id}`);
    } catch (error) {
      toast.error(t.messages.saveError);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="btn-outline p-2">
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <h1 className="page-title">
          {isEdit ? t.documents.editDocument : t.documents.newDocument}
        </h1>
        <button
          type="button"
          onClick={() => setShowPreview((v) => !v)}
          className={`ml-auto inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition ${
            showPreview
              ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
              : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
          }`}
          title={showPreview ? 'Masquer l\'aperçu PDF' : 'Afficher l\'aperçu PDF'}
        >
          <DocumentTextIcon className="h-4 w-4" />
          {showPreview ? 'Aperçu PDF activé' : 'Aperçu PDF'}
        </button>
      </div>

      <div className={showPreview ? 'grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6' : ''}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Informations du document
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="label">{t.documents.typeDocument} *</label>
              <select
                {...register('typeDocument', { required: t.validation.required })}
                className="input-field"
              >
                {Object.entries(TYPE_DOCUMENT_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">{t.documents.dateDocument} *</label>
              <input
                type="date"
                {...register('dateDocument', { required: t.validation.required })}
                className={`input-field ${errors.dateDocument ? 'input-error' : ''}`}
              />
            </div>

            <div>
              <label className="label">{t.documents.dateEcheance}</label>
              <input
                type="date"
                {...register('dateEcheance')}
                className="input-field"
              />
            </div>

            <div className="md:col-span-2 lg:col-span-3 relative">
              <label className="label">{t.documents.client} *</label>
              <input
                type="text"
                value={clientSearch}
                onChange={(e) => handleClientSearch(e.target.value)}
                onFocus={() => setShowClientDropdown(true)}
                placeholder={t.documents.selectClient}
                className={`input-field ${!selectedClient && errors.clientId ? 'input-error' : ''}`}
              />
              <input type="hidden" {...register('clientId', { required: true, min: 1 })} />
              {showClientDropdown && (filteredClients.length > 0 || (clientSearch.trim() && !clientExactMatchExists())) && (
                <div className="absolute z-30 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredClients.slice(0, 20).map((client) => (
                    <button
                      key={client.id}
                      type="button"
                      onClick={() => selectClient(client)}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-primary-50 hover:text-primary-700 flex justify-between items-center"
                    >
                      <div>
                        <span className="font-medium">{client.raisonSociale}</span>
                        <span className="text-gray-500 ml-2">({client.code})</span>
                      </div>
                      {client.ville && (
                        <span className="text-xs text-gray-400">{client.ville}</span>
                      )}
                    </button>
                  ))}
                  {clientSearch.trim() && !clientExactMatchExists() && (
                    <button
                      type="button"
                      onClick={handleQuickCreateClient}
                      className="w-full text-left px-4 py-2 text-sm text-primary-700 bg-primary-50 hover:bg-primary-100 border-t border-gray-200 flex items-center gap-2"
                    >
                      <PlusIcon className="h-4 w-4" />
                      <span>Créer le client «&nbsp;<span className="font-medium">{clientSearch.trim()}</span>&nbsp;»</span>
                    </button>
                  )}
                </div>
              )}
              {selectedClient && (
                <p className="mt-1 text-xs text-gray-500">
                  MF: {selectedClient.matriculeFiscal || 'N/A'} | {selectedClient.ville}
                  {selectedClient.tauxRetenueSource ? ` | RS: ${selectedClient.tauxRetenueSource}%` : ''}
                </p>
              )}
            </div>

            <div>
              <label className="label">{t.documents.modePaiement} *</label>
              <select
                {...register('modePaiement', { required: t.validation.required })}
                className="input-field"
              >
                {Object.entries(MODE_PAIEMENT_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">{t.documents.referenceExterne}</label>
              <input
                {...register('referenceExterne')}
                className="input-field"
              />
            </div>

            {templates.length > 0 && (
              <div>
                <label className="label">Modèle</label>
                <select
                  {...register('templateId', { valueAsNumber: true })}
                  className="input-field"
                >
                  {templates.map((tpl) => (
                    <option key={tpl.id} value={tpl.id}>
                      {tpl.name}{tpl.isDefault ? ' (par défaut)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        <div className="card overflow-visible">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {t.documents.lignes}
            </h2>
            <button type="button" onClick={addLine} className="btn-secondary">
              <PlusIcon className="h-4 w-4 mr-1" />
              {t.documents.addLine}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase w-1/4">
                    {t.documents.article}
                  </th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase w-20">
                    {t.documents.quantite}
                  </th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase w-28">
                    {t.documents.prixUnitaire}
                  </th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase w-20">
                    {t.documents.remise}
                  </th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase w-24">
                    {t.documents.tauxTva}
                  </th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase w-12">
                    FODEC
                  </th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase w-32">
                    {t.documents.montantHT}
                  </th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase w-28">
                    {t.documents.montantFodec}
                  </th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase w-28">
                    {t.documents.montantTva}
                  </th>
                  <th className="px-2 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {fields.map((field, index) => {
                  const ligne = watchedLignes?.[index];
                  const lineCalc = ligne
                    ? calculateLineTotals(
                        ligne.quantite || 0,
                        ligne.prixUnitaireHT || 0,
                        ligne.tauxTva || TauxTva.TVA_19,
                        ligne.fodec || false,
                        ligne.remise || 0
                      )
                    : { montantHT: 0, montantTva: 0, montantFodec: 0, montantTTC: 0 };

                  return (
                    <tr key={field.id} className="border-b border-gray-100">
                      <td className="px-2 py-2">
                        <ArticlePickerCell
                          articles={articles}
                          selectedArticleId={watchedLignes?.[index]?.articleId || 0}
                          placeholder={t.documents.selectArticle}
                          onSelect={(a) => selectArticle(index, a.id)}
                          onCreate={(designation) => handleQuickCreateArticle(index, designation)}
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="number"
                          step="1"
                          min="0"
                          {...register(`lignes.${index}.quantite` as const, { valueAsNumber: true })}
                          className="input-field text-sm text-center"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="number"
                          step="0.001"
                          min="0"
                          {...register(`lignes.${index}.prixUnitaireHT` as const, { valueAsNumber: true })}
                          className="input-field text-sm text-right"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="number"
                          step="1"
                          min="0"
                          max="100"
                          {...register(`lignes.${index}.remise` as const, { valueAsNumber: true })}
                          className="input-field text-sm text-center"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <select
                          {...register(`lignes.${index}.tauxTva` as const)}
                          className="input-field text-sm"
                        >
                          {Object.entries(TVA_LABELS).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          {...register(`lignes.${index}.fodec` as const)}
                          className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <span className="text-sm tnd-amount">{formatTND(lineCalc.montantHT)}</span>
                      </td>
                      <td className="px-2 py-2 text-right">
                        <span className="text-sm tnd-amount">{formatTND(lineCalc.montantFodec)}</span>
                      </td>
                      <td className="px-2 py-2 text-right">
                        <span className="text-sm tnd-amount">{formatTND(lineCalc.montantTva)}</span>
                      </td>
                      <td className="px-2 py-2">
                        {fields.length > 1 && (
                          <button
                            type="button"
                            onClick={() => remove(index)}
                            className="text-red-500 hover:text-red-700 p-1"
                            title={t.documents.removeLine}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t.common.notes}</h2>
            <textarea
              {...register('notes')}
              rows={4}
              className="input-field"
              placeholder="Notes ou conditions..."
            />
            <label className="mt-4 flex items-start gap-3 rounded-lg border border-gray-200 p-3 cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                {...register('factureElectronique')}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <div className="text-sm">
                <div className="font-semibold text-gray-900">{t.documents.eInvoiceToggle}</div>
                <div className="text-xs text-gray-500 mt-1">{t.documents.eInvoiceToggleHint}</div>
              </div>
            </label>
            {/* Le timbre ne concerne que les factures et les devis — inutile de proposer
                l'option sur un avoir ou un bon de livraison, qui n'en portent jamais. */}
            {(watchedTypeDocument === TypeDocument.FACTURE
              || watchedTypeDocument === TypeDocument.DEVIS) && (
              <label className="mt-3 flex items-start gap-3 rounded-lg border border-gray-200 p-3 cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  {...register('appliquerTimbreFiscal')}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <div className="text-sm">
                  <div className="font-semibold text-gray-900">{t.documents.timbreFiscalToggle}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {t.documents.timbreFiscalToggleHint} ({formatTND(companyTimbreFiscal)})
                  </div>
                </div>
              </label>
            )}
          </div>

          <div className="card bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t.common.total}</h2>
            <div className="space-y-2">
              {totals.totalRemise > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t.documents.remiseTotale}</span>
                  <span>- {formatTND(totals.totalRemise)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t.documents.totalHT}</span>
                <MoneyDisplay amount={totals.totalHT} />
              </div>
              {totals.totalFodec > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t.documents.totalFodec}</span>
                  <MoneyDisplay amount={totals.totalFodec} />
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t.documents.totalTva}</span>
                <MoneyDisplay amount={totals.totalTva} />
              </div>
              <div className="border-t border-gray-300 pt-2 flex justify-between text-sm font-medium">
                <span className="text-gray-700">{t.documents.totalTTC}</span>
                <MoneyDisplay amount={totals.totalTTC} />
              </div>
              {totals.timbreFiscal > 0 ? (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    {t.documents.timbreFiscal}
                  </span>
                  <MoneyDisplay amount={totals.timbreFiscal} />
                </div>
              ) : (watchedTypeDocument === TypeDocument.FACTURE
                  || watchedTypeDocument === TypeDocument.DEVIS) && (
                <div className="flex justify-between text-sm text-gray-400">
                  <span>{t.documents.timbreFiscal}</span>
                  <span>{t.documents.timbreFiscalNonApplique}</span>
                </div>
              )}
              {totals.retenueSource > 0 && (
                <div className="flex justify-between text-sm text-red-600">
                  <span>
                    {t.documents.retenueSource}
                    <span className="text-xs ml-1">
                      ({selectedClient?.tauxRetenueSource}%)
                    </span>
                  </span>
                  <span>- {formatTND(totals.retenueSource)}</span>
                </div>
              )}
              <div className="border-t-2 border-primary-500 pt-3 flex justify-between">
                <span className="text-lg font-bold text-gray-900">{t.documents.netAPayer}</span>
                <MoneyDisplay amount={totals.netAPayer} size="lg" className="text-primary-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          <button type="button" onClick={() => navigate(-1)} className="btn-outline">
            {t.common.cancel}
          </button>
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? t.common.loading : t.common.save}
          </button>
        </div>
      </form>

      {showPreview && (
        <aside className="hidden xl:block">
          <div className="sticky top-4 card p-0 overflow-hidden border border-gray-200">
            <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <DocumentTextIcon className="h-4 w-4" />
                Aperçu PDF
                {previewLoading && (
                  <span className="ml-2 inline-flex items-center gap-1 text-xs font-normal text-gray-500">
                    <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                    Actualisation…
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setShowPreview(false)}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Masquer
              </button>
            </div>
            <div className="bg-gray-100" style={{ height: 'calc(100vh - 8rem)' }}>
              {previewError && !previewUrl ? (
                <div className="h-full flex items-center justify-center p-6 text-center text-sm text-gray-500">
                  {previewError}
                </div>
              ) : previewUrl ? (
                <iframe
                  title="Aperçu PDF"
                  src={previewUrl}
                  className="w-full h-full border-0"
                  data-testid="document-pdf-preview"
                />
              ) : (
                <div className="h-full flex items-center justify-center p-6 text-center text-sm text-gray-400">
                  Génération de l'aperçu…
                </div>
              )}
            </div>
          </div>
        </aside>
      )}
      </div>
    </div>
  );
}

export default DocumentForm;
